/**
 * Token Storage Utility
 * - Refresh Token: Stored in Cookie (`refresh_token`) with LocalStorage fallback
 * - Access Token: Stored in LocalStorage (`access_token`, `auth_token`) & Zustand store
 */

export const REFRESH_TOKEN_COOKIE_NAME = 'refresh_token'
export const ACCESS_TOKEN_STORAGE_KEY = 'access_token'

/**
 * Set Refresh Token in Cookie (document.cookie) and LocalStorage
 */
export function setRefreshTokenCookie(token: string, days = 30): void {
  if (typeof document !== 'undefined') {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString()
    const secureFlag = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${REFRESH_TOKEN_COOKIE_NAME}=${encodeURIComponent(token)}; expires=${expires}; path=/; SameSite=Lax${secureFlag}`
  }
  localStorage.setItem(REFRESH_TOKEN_COOKIE_NAME, token)
}

/**
 * Get Refresh Token from Cookie (with fallback to LocalStorage)
 */
export function getRefreshToken(): string | null {
  if (typeof document !== 'undefined' && document.cookie) {
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [key, value] = cookie.trim().split('=')
      if (key === REFRESH_TOKEN_COOKIE_NAME && value) {
        return decodeURIComponent(value)
      }
    }
  }
  return localStorage.getItem(REFRESH_TOKEN_COOKIE_NAME) || null
}

/**
 * Remove Refresh Token from Cookie & LocalStorage
 */
export function removeRefreshTokenCookie(): void {
  if (typeof document !== 'undefined') {
    document.cookie = `${REFRESH_TOKEN_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
  }
  localStorage.removeItem(REFRESH_TOKEN_COOKIE_NAME)
}

/**
 * Set Access Token in LocalStorage
 */
export function setAccessToken(token: string): void {
  localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token)
  localStorage.setItem('auth_token', token)
}

/**
 * Get Access Token from LocalStorage
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) || localStorage.getItem('auth_token') || null
}

/**
 * Remove Access Token from LocalStorage
 */
export function removeAccessToken(): void {
  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY)
  localStorage.removeItem('auth_token')
}

// Backwards compatibility functions
export const setAccessTokenCookie = setAccessToken
export const setRefreshToken = setRefreshTokenCookie
export const removeAccessTokenCookie = removeAccessToken
export const removeRefreshToken = removeRefreshTokenCookie

/**
 * Save Auth Tokens (Access Token -> LocalStorage/Zustand, Refresh Token -> Cookie)
 */
export function saveAuthTokens(accessToken?: string, refreshToken?: string): void {
  if (accessToken) {
    setAccessToken(accessToken)
  }
  if (refreshToken) {
    setRefreshTokenCookie(refreshToken)
  }
}

/**
 * Clear All Auth Tokens
 */
export function clearAuthTokens(): void {
  removeAccessToken()
  removeRefreshTokenCookie()
}
